import React, { useEffect, useState, useContext, useMemo, Fragment } from 'react';
import need_assist_red from 'assests/svg/need_assist_red.svg';
import need_assist_black from 'assests/svg/need_assist_black.svg';
import Accordion from 'b2b-ui-components-lib/lib/components/accordion';

import {
  getContractPeriod,
  getLeadStatus,
  getPlanPrice,
  numberWithCommas,
  getPlanDetail,
  monthsToYear,
  getHardwareType,
  getOldLineItemDetail,
  fsNeedAssistanceOnClick
} from 'home/view/upgrade-order/utils';
import { LEAD_STATUS, HARDWARE_TYPE } from 'home/view/upgrade-order/constants';

import { CONTEXT } from 'home/view/upgrade-order/context';

import FeasibilityStatus from 'home/view/upgrade-order/components/common/FeasibilityStatus';
import PlanConfiguration from '../PlanConfigration/PlanConfigration';

const CONTRACT_PERIOD_LIST = [
  {
    key: 1,
    displayName: '1 year'
  },
  {
    key: 2,
    displayName: '2 years'
  },
  {
    key: 3,
    displayName: '3 years'
  },
  {
    key: 4,
    displayName: '4 years'
  },
  {
    key: 5,
    displayName: '5 years'
  }
];

const DEFAULT_MONEY = 'N/A';

const NeedAssistComp = ({ disabled, setShowNeedAssistPopup, blockJourney, leadData, userType }) => {
  const isIconDisabled = () => {
    if (blockJourney) {
      return false;
    }

    return disabled;
  };

  const getStyles = () => {
    const defaultStyle = {
      fontSize: '14px',
      display: 'flex',
      borderRight: '1.5px solid #D5D7D6',
      paddingRight: '12px'
    };

    const conditionalStyle = () => {
      return isIconDisabled()
        ? {
            opacity: '0.5'
          }
        : {
            color: '#CC000A',
            cursor: 'pointer'
          };
    };

    return {
      ...defaultStyle,
      ...conditionalStyle()
    };
  };

  return (
    <div className="tooltip_wrap need-assist-wrapper">
      <div className="tooltip_list">
        <div
          style={getStyles()}
          role={isIconDisabled() ? 'caption' : 'button'}
          onClick={() => {
            !isIconDisabled() && setShowNeedAssistPopup(true);
            fsNeedAssistanceOnClick(leadData, userType);
          }}
        >
          <img src={isIconDisabled() ? need_assist_black : need_assist_red} alt="need_assistance" /> Need Assistance
        </div>
        {!isIconDisabled() && (
          <div className="tooltip need-assist-tooltip">
            <div className="tooltip_content">{`For any assistance, contact the Key Account Manager by clicking ‘Need Assistance’`}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const TitleComponent = React.memo(
  ({ isExpanded, pricingDetails, totalCost, setShowNeedAssistPopup, disabled, userType, selectedContractPeriod, blockJourney, lowerPriceBw, leadData, setToastMsg }) => {
    const { standardRate, rental, hardwareType, leadStatus } = pricingDetails;
    const contractPeriod = (selectedContractPeriod && selectedContractPeriod.key) || CONTRACT_PERIOD_LIST[0].key;
    const isNonStandardHardware = (hardwareType && hardwareType === HARDWARE_TYPE.NON_STANDARD) || false;
    const isZeroRateCard = parseInt(rental) === 0 && !standardRate;
    return (
      <React.Fragment>
        {isExpanded ? (
          <div className="upgrade-header-open">
            <div className="title pad-bottom-0">Plan & Configuration</div>
            <label className="field_label">All fields are mandatory unless marked optional.</label>
            {!lowerPriceBw && parseInt(rental) !== 0 && !standardRate && <div className="sub-title">Pricing shown below is as per the customer-agreed rates.</div>}
          </div>
        ) : (
          <Fragment>
            <div className="upgrade-header-open">
              <div className="title pad-bottom-0">Plan & Configuration</div>
              <label className="field_label">The following the plan & configuration details for the new installation address.</label>
            </div>
            <ul className="upgrade-header-closed">
              <li key="total-price">{`Total Contract Value: ₹ ${totalCost ? numberWithCommas(totalCost) : DEFAULT_MONEY}`}</li>
              <li key="otc">{`One Time Charge: ₹ ${
                pricingDetails.leadStatus !== LEAD_STATUS.LEAD_SUBMITTED && lowerPriceBw
                  ? DEFAULT_MONEY
                  : pricingDetails.oneTimeCharge
                  ? numberWithCommas(pricingDetails.oneTimeCharge)
                  : pricingDetails.rental
                  ? pricingDetails.oneTimeCharge
                  : DEFAULT_MONEY
              }`}</li>
              <li key="arc">{`Annual Recurring Charge: ₹ ${
                pricingDetails.leadStatus !== LEAD_STATUS.LEAD_SUBMITTED && lowerPriceBw
                  ? DEFAULT_MONEY
                  : pricingDetails.rental
                  ? numberWithCommas(pricingDetails.rental)
                  : DEFAULT_MONEY
              }`}</li>
              {contractPeriod && <li key="contract-period">{`Contract Period: ${contractPeriod} ${contractPeriod > 1 ? 'Years' : 'Year'}`}</li>}
            </ul>
          </Fragment>
        )}
        <FeasibilityStatus
          pricingTag
          leadStatus={pricingDetails.leadStatus}
          isNonStandardHardware={isNonStandardHardware}
          isZeroRateCard={isZeroRateCard}
          lowerPriceBw={lowerPriceBw}
          setToastMsg={setToastMsg}
        />
      </React.Fragment>
    );
  }
);

const PricingQuotation = (props) => {
  const {
    leadData = {},
    selectedContractPeriod = {},
    setSelectedContractPeriod = () => {},
    setShowNeedAssistPopup = () => {},
    selectedBandwidth = {},
    blockJourney = false,
    lowerPriceBw = false,
    setLowerPriceBw = () => {}
  } = props;

  const getPricing = () =>
    useMemo(() => {
      let rental = '';
      let oneTimeCharge = '';
      let standardRate = false;

      if (
        selectedBandwidth &&
        selectedBandwidth.key &&
        (leadData.leadStatus === LEAD_STATUS.FEASIBILITY_COMPLETED || leadData.leadStatus === LEAD_STATUS.REQUIRED_PRICE_NEGOTIATION)
      ) {
        rental = selectedBandwidth && selectedBandwidth.plan && selectedBandwidth.plan.prices && selectedBandwidth.plan.prices.RATE.charge;
        oneTimeCharge = selectedBandwidth && selectedBandwidth.plan && selectedBandwidth.plan.prices && selectedBandwidth.plan.prices.RATE.otc;
        standardRate = selectedBandwidth && selectedBandwidth.plan && selectedBandwidth.plan.standardRate;
      } else {
        rental = getPlanPrice(leadData) && getPlanPrice(leadData).rental;
        oneTimeCharge = getPlanPrice(leadData) && getPlanPrice(leadData).oneTimeCharge;
        standardRate = getPlanDetail(leadData) && getPlanDetail(leadData).standardRate;
      }

      if (rental < (getOldLineItemDetail(leadData) && getOldLineItemDetail(leadData).price && getOldLineItemDetail(leadData).price.rental)) {
        setLowerPriceBw(true);
      } else {
        setLowerPriceBw(false);
      }

      return {
        rental,
        oneTimeCharge,
        standardRate
      };
    }, [selectedBandwidth, leadData]);

  const pricingDetails = {
    leadStatus: getLeadStatus(leadData),
    contractPeriod: getContractPeriod(leadData), // in months
    rental: getPricing().rental,
    oneTimeCharge: getPricing().oneTimeCharge,
    standardRate: getPricing().standardRate,
    hardwareType: getHardwareType(leadData)
  };

  const [accordionMeta, setAccordionMeta] = useState({
    isExpanded: true
  });
  const [totalCost, setTotalCost] = useState(DEFAULT_MONEY);

  const { userType, setToastMsg } = useContext(CONTEXT);

  useEffect(() => {
    if (pricingDetails.contractPeriod) {
      const yrValue = Math.floor(monthsToYear(pricingDetails.contractPeriod));
      const selectedItem = CONTRACT_PERIOD_LIST.find((item) => item.key === yrValue);
      setSelectedContractPeriod(selectedItem ? selectedItem : { key: yrValue, displayName: `${yrValue} years` });
    } else {
      setSelectedContractPeriod(CONTRACT_PERIOD_LIST[0]);
    }
  }, [pricingDetails.contractPeriod]);

  useEffect(() => {
    const leadStatus = pricingDetails.leadStatus;

    const closedStatus = leadStatus === LEAD_STATUS.KYC_IN_PROGRESS || leadStatus === LEAD_STATUS.KYC_SUBMITTED;

    if (closedStatus) {
      setAccordionMeta({
        isExpanded: false
      });
    }
  }, [pricingDetails.leadStatus]);

  useEffect(() => {
    const totalSum = pricingDetails.rental * selectedContractPeriod.key + pricingDetails.oneTimeCharge;
    setTotalCost(totalSum);
  }, [selectedContractPeriod, pricingDetails.rental, pricingDetails.oneTimeCharge]);

  const handleContractPeriodChange = (value) => {
    setSelectedContractPeriod(value);
  };

  const isDisabled = () => {
    return !(pricingDetails.leadStatus === LEAD_STATUS.FEASIBILITY_COMPLETED);
  };

  return (
    <div className="pnq-details upgrade-accordion">
      <Accordion
        autoScrollOnExpand
        title={
          <TitleComponent
            isExpanded={accordionMeta.isExpanded}
            pricingDetails={pricingDetails}
            totalCost={totalCost}
            setShowNeedAssistPopup={setShowNeedAssistPopup}
            userType={userType}
            disabled={isDisabled()}
            selectedContractPeriod={selectedContractPeriod}
            blockJourney={blockJourney}
            lowerPriceBw={lowerPriceBw}
            leadData={leadData}
            setToastMsg={setToastMsg}
          />
        }
        isExpanded={accordionMeta.isExpanded}
        onExpandClick={() =>
          setAccordionMeta((accordionMeta) => ({
            ...accordionMeta,
            isExpanded: !accordionMeta.isExpanded
          }))
        }
        variant="round"
      >
        <div className="upgrade-body">
          <PlanConfiguration
            selectedContractPeriod={selectedContractPeriod}
            handleContractPeriodChange={handleContractPeriodChange}
            CONTRACT_PERIOD_LIST={CONTRACT_PERIOD_LIST}
            isDisabled={isDisabled}
            arc={
              pricingDetails.leadStatus !== LEAD_STATUS.LEAD_SUBMITTED && lowerPriceBw
                ? DEFAULT_MONEY
                : pricingDetails.rental
                ? numberWithCommas(pricingDetails.rental)
                : DEFAULT_MONEY
            }
            otc={
              pricingDetails.leadStatus !== LEAD_STATUS.LEAD_SUBMITTED && lowerPriceBw
                ? DEFAULT_MONEY
                : pricingDetails.oneTimeCharge
                ? numberWithCommas(pricingDetails.oneTimeCharge)
                : pricingDetails.rental
                ? pricingDetails.oneTimeCharge
                : DEFAULT_MONEY
            }
            totalCost={pricingDetails.leadStatus !== LEAD_STATUS.LEAD_SUBMITTED && lowerPriceBw ? DEFAULT_MONEY : totalCost ? numberWithCommas(totalCost) : DEFAULT_MONEY}
            setShowNeedAssistPopup={setShowNeedAssistPopup}
            {...props}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default PricingQuotation;
